/**
 * C-Market Intel · Serving performance — how fast Market Intel answers what its
 * clients actually ask (ADR-033 §1), against each journey's budget.
 *
 * WHY NOT THE SQL PLANS ANY MORE. This tab used to EXPLAIN the audit's own
 * queries. Nobody outside the team waits on those. Dispatchers, agents and the
 * mobile app wait on J1–J4, so those are what is measured here — through the
 * same functions the public routes call, on real data.
 *
 * Two numbers per journey, because they answer different questions:
 *   - Measured — a fresh run on this instance, now. "Is it fast when warm?"
 *   - Real traffic — what requests to the route cost over the last hour, the
 *     server's time before the network. "Is it fast for the people using it?"
 */

import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import Chip from '@mui/joy/Chip'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import type { LoadPipelineLaneHistory, LoadPipelineLatency, LoadPipelineServingJourney, LoadPipelineServingResponse } from '@numeo/types-core'
import * as React from 'react'
import { loadPipelineApi } from 'api/loadPipeline'
import { LaneSeriesChart } from './components/LaneSeriesChart'
import { SnapshotAge, money } from './components/marketIntel'
import { ErrorState, Loading, Stat, StatRow } from './components/shared'

function ms(v: number): string {
    return v < 1 ? `${(v * 1000).toFixed(0)} µs` : `${v.toFixed(v < 10 ? 2 : 1)} ms`
}

/** Green inside the budget, amber within 2× of it, red beyond — judged on p95, the number a user feels. */
function LatencyCell({ latency, budgetMs }: { latency: LoadPipelineLatency | null; budgetMs: number }) {
    if (!latency) return <Typography level="body-sm">—</Typography>
    const color = latency.p95Ms <= budgetMs ? 'success' : latency.p95Ms <= budgetMs * 2 ? 'warning' : 'danger'
    return (
        <Box>
            <Chip size="sm" variant="soft" color={color}>
                p95 {ms(latency.p95Ms)}
            </Chip>
            <Typography level="body-xs" textColor="text.tertiary">
                p50 {ms(latency.p50Ms)} · {latency.samples} {latency.samples === 1 ? 'run' : 'runs'}
            </Typography>
        </Box>
    )
}

function JourneyRow({ journey }: { journey: LoadPipelineServingJourney }) {
    return (
        <tr>
            <td>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip size="sm" variant="outlined">
                        {journey.journey}
                    </Chip>
                    <Typography level="body-sm" fontWeight="lg">
                        {journey.label}
                    </Typography>
                </Box>
                <Typography level="body-xs" textColor="text.tertiary">
                    “{journey.question}” · <code>{journey.route}</code>
                </Typography>
            </td>
            <td>
                <Typography level="body-sm">&lt; {journey.budgetMs} ms</Typography>
            </td>
            <td>
                <LatencyCell latency={journey.measured} budgetMs={journey.budgetMs} />
            </td>
            <td>
                <LatencyCell latency={journey.observed} budgetMs={journey.budgetMs} />
            </td>
            <td>
                <Typography level="body-xs" textColor={journey.error ? 'danger.plainColor' : 'text.secondary'}>
                    {journey.error ?? journey.detail ?? '—'}
                </Typography>
            </td>
        </tr>
    )
}

const median = (values: number[]): number | null => {
    if (values.length === 0) return null
    const sorted = [...values].sort((a, b) => a - b)
    const mid = Math.floor(sorted.length / 2)
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

/** J4 as a dispatcher reads it: today, yesterday, the last week, against the two-week median. */
function HistoryCard({ history }: { history: LoadPipelineLaneHistory }) {
    const days = history.days
    const at = (daysAgo: number) => days.find((d) => d.daysAgo === daysAgo)?.p50 ?? null
    const lastWeek = median(days.filter((d) => d.daysAgo < 7 && d.p50 != null).map((d) => d.p50 as number))
    return (
        <Card sx={{ mt: 3 }}>
            <Typography level="title-md">Busiest lane — price history, as J4 returned it</Typography>
            <Typography level="body-xs" textColor="text.tertiary" sx={{ mb: 1 }}>
                {history.equipment} · {history.observations.toLocaleString()} loads · a median per day; a day below the sample floor is a gap, never zero
            </Typography>
            <StatRow>
                <Stat label="Today" value={money(at(0))} />
                <Stat label="Yesterday" value={money(at(1))} />
                <Stat label="7-day median" value={money(lastWeek)} hint="of the last 7 daily medians, today included" />
                <Stat label="14 days" value={money(history.windowMedian)} hint="the lane's window median" />
            </StatRow>
            <LaneSeriesChart days={days} />
        </Card>
    )
}

export default function LoadPipelineServingPerfPage() {
    const [data, setData] = React.useState<LoadPipelineServingResponse | null>(null)
    const [error, setError] = React.useState<unknown>(null)
    const [loading, setLoading] = React.useState(false)

    const measure = React.useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            setData(await loadPipelineApi.getServing())
        } catch (err) {
            setError(err)
        } finally {
            setLoading(false)
        }
    }, [])

    // Cheap enough to run on open: J1, J2 and J4 read memory, and J3 (the one live query) runs three times.
    React.useEffect(() => {
        void measure()
    }, [measure])

    return (
        <Box>
            <Typography level="body-sm" sx={{ mb: 2, color: 'text.tertiary' }}>
                How fast Market Intel answers the questions its clients ask — each journey against its ADR-033 budget.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button size="sm" onClick={measure} loading={loading}>
                    Measure again
                </Button>
                {data ? <SnapshotAge provenance={{ builtAt: data.builtAt, window: data.window, minSamples: null }} /> : null}
                {data ? (
                    <Typography level="body-xs" textColor="text.tertiary">
                        measured {new Date(data.measuredAt).toLocaleTimeString()}
                    </Typography>
                ) : null}
            </Box>

            {loading && !data ? <Loading /> : null}
            {error ? <ErrorState error={error} /> : null}

            {data ? (
                <>
                    <Card>
                        <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                            <Table size="sm" stickyHeader>
                                <thead>
                                    <tr>
                                        <th style={{ width: 340 }}>Journey</th>
                                        <th style={{ width: 90 }}>Budget</th>
                                        <th style={{ width: 150 }}>Measured now</th>
                                        <th style={{ width: 150 }}>Real traffic · last hour</th>
                                        <th>What ran</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.journeys.map((journey) => (
                                        <JourneyRow key={journey.key} journey={journey} />
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>
                        <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: 1 }}>
                            Server time only — the network to the client is on top. Judged on p95: green inside the budget, amber within 2×, red beyond.
                        </Typography>
                    </Card>
                    {data.history ? <HistoryCard history={data.history} /> : null}
                </>
            ) : null}
        </Box>
    )
}
